const STAR_COLORS = {
  o: '#9bb0ff',
  b: '#aabfff',
  a: '#cad7ff',
  f: '#f8f7ff',
  g: '#fff4ea',
  k: '#ffd2a1',
  m: '#ffcc6f',
}

const PLANET_COLORS = {
  planet_earth: '#4a90d9',
  planet_water: '#2e86ab',
  planet_dead: '#8b7355',
  planet_acid: '#7cb342',
  planet_frozen: '#b3e5fc',
}

const SATELLITE_COLORS = {
  satellite_earth: '#9e9e9e',
  satellite_water: '#81d4fa',
  satellite_dead: '#6d4c41',
  satellite_acid: '#aed581',
  satellite_frozen: '#e1f5fe',
}

const ASTEROID_COLOR = '#757575'
const SHIP_COLOR = '#ff9800'
const STATION_COLOR = '#e91e63'
const PROBE_COLOR = '#9c27b0'
const PLAYER_HIGHLIGHT = '#ffeb3b'

const PADDING = 40
const MIN_BODY_RADIUS = 4
const MAX_BODY_RADIUS = 18

function degToRad (deg) {
  return (deg * Math.PI) / 180
}

function defaultFi (index, total) {
  return (360 / Math.max(total, 1)) * index
}

function defaultR (index, maxR, bodyCount) {
  return maxR * (0.3 + (0.6 * (index + 1)) / Math.max(bodyCount, 1))
}

function orbitRadius (orbit, index, maxR, bodyCount) {
  if (orbit && orbit.r != null && orbit.r > 0) return orbit.r
  return defaultR(index, maxR, bodyCount)
}

function orbitAngle (orbit, index, bodyCount) {
  if (orbit && orbit.fi != null) return orbit.fi
  return defaultFi(index, bodyCount)
}

function maxOrbitRadius (bodies) {
  let max = 1
  const bodyCount = bodies.length
  bodies.forEach((body, index) => {
    const r = orbitRadius(body.orbit, index, 100, bodyCount)
    if (r > max) max = r
  })
  return max
}

function scaleFactor (width, height, maxR) {
  const size = Math.min(width, height)
  return (size / 2 - PADDING) / maxR
}

function ellipseRadii (r, scale, orbit) {
  const i = orbit?.i || 0
  const ifi = orbit?.ifi || 0
  if (i === 0 && ifi === 0) {
    return { rx: r * scale, ry: r * scale, rotation: 0 }
  }
  const flatten = Math.max(0.3, Math.cos(degToRad(i)))
  return {
    rx: r * scale,
    ry: r * scale * flatten,
    rotation: degToRad(ifi),
  }
}

function bodyPosition (cx, cy, r, scale, orbit, index, bodyCount) {
  const fi = orbitAngle(orbit, index, bodyCount)
  const { rx, ry, rotation } = ellipseRadii(r, scale, orbit)
  const angle = degToRad(fi) + rotation
  return {
    x: cx + rx * Math.cos(angle),
    y: cy + ry * Math.sin(angle),
  }
}

function starColor (star) {
  return STAR_COLORS[star.star_spectr] || '#fff4ea'
}

function bodyColor (body) {
  if (body.orbiter_type === 'Spaceship') {
    const colors = { ship: SHIP_COLOR, station: STATION_COLOR, probe: PROBE_COLOR }
    return colors[body.kind] || SHIP_COLOR
  }
  switch (body.kind) {
    case 'planet':
      return PLANET_COLORS[body.planet_kind] || '#4a90d9'
    case 'satellite':
      return SATELLITE_COLORS[body.satellite_kind] || '#9e9e9e'
    case 'asteroid':
      return ASTEROID_COLOR
    default:
      return '#bdbdbd'
  }
}

function bodyRadius (body, starRadius) {
  if (body.orbiter_type === 'Spaceship') return MIN_BODY_RADIUS + 2
  const base = body.radius > 0 ? body.radius : starRadius * 0.15
  return Math.min(MAX_BODY_RADIUS, Math.max(MIN_BODY_RADIUS, base * 0.12))
}

function drawOrbit (ctx, cx, cy, r, scale, orbit) {
  const { rx, ry, rotation } = ellipseRadii(r, scale, orbit)
  ctx.save()
  ctx.translate(cx, cy)
  ctx.rotate(rotation)
  ctx.beginPath()
  ctx.ellipse(0, 0, rx, ry, 0, 0, Math.PI * 2)
  ctx.strokeStyle = 'rgba(255, 255, 255, 0.2)'
  ctx.lineWidth = 1
  ctx.stroke()
  ctx.restore()
}

function drawStar (ctx, cx, cy, star) {
  const r = Math.min(28, Math.max(14, (star.radius || 120) * 0.15))
  const gradient = ctx.createRadialGradient(cx, cy, 0, cx, cy, r)
  gradient.addColorStop(0, '#ffffff')
  gradient.addColorStop(0.4, starColor(star))
  gradient.addColorStop(1, 'rgba(255, 200, 100, 0)')
  ctx.beginPath()
  ctx.arc(cx, cy, r, 0, Math.PI * 2)
  ctx.fillStyle = gradient
  ctx.fill()
}

function drawBody (ctx, x, y, body, isPlayer) {
  const r = bodyRadius(body, 120)
  ctx.save()
  if (isPlayer) {
    ctx.shadowColor = PLAYER_HIGHLIGHT
    ctx.shadowBlur = 12
  }
  if (body.orbiter_type === 'Spaceship') {
    ctx.fillStyle = bodyColor(body)
    ctx.strokeStyle = isPlayer ? PLAYER_HIGHLIGHT : '#ffffff'
    ctx.lineWidth = isPlayer ? 2 : 1
    const s = r * 1.4
    ctx.beginPath()
    if (body.kind === 'station') {
      ctx.rect(x - s / 2, y - s / 2, s, s)
    } else {
      ctx.moveTo(x, y - s)
      ctx.lineTo(x + s, y + s * 0.6)
      ctx.lineTo(x - s, y + s * 0.6)
      ctx.closePath()
    }
    ctx.fill()
    ctx.stroke()
  } else {
    ctx.beginPath()
    ctx.arc(x, y, r, 0, Math.PI * 2)
    ctx.fillStyle = bodyColor(body)
    ctx.fill()
    if (isPlayer) {
      ctx.strokeStyle = PLAYER_HIGHLIGHT
      ctx.lineWidth = 2
      ctx.stroke()
    }
  }
  ctx.restore()

  ctx.fillStyle = 'rgba(255, 255, 255, 0.85)'
  ctx.font = '11px sans-serif'
  ctx.textAlign = 'center'
  ctx.fillText(body.name, x, y + r + 14)
}

function computeBodyPositions (cx, cy, bodies, maxR, bodyCount, scale) {
  const bodyPositions = new Map()

  bodies.forEach((body, index) => {
    if (body.parent_id) return

    const r = orbitRadius(body.orbit, index, maxR, bodyCount)
    const pos = bodyPosition(cx, cy, r, scale, body.orbit, index, bodyCount)
    bodyPositions.set(body.id, pos)
  })

  bodies.forEach((body, index) => {
    if (!body.parent_id) return

    const parentPos = bodyPositions.get(body.parent_id)
    if (!parentPos) return

    const r = orbitRadius(body.orbit, index, maxR, bodyCount)
    const localPos = bodyPosition(0, 0, r, scale, body.orbit, index, bodyCount)
    bodyPositions.set(body.id, {
      x: parentPos.x + localPos.x,
      y: parentPos.y + localPos.y,
    })
  })

  return bodyPositions
}

function drawSolarSystemContent (ctx, cx, cy, star, bodies, maxR, bodyCount, scale, bodyPositions, playerOrbiterId) {
  drawStar(ctx, cx, cy, star)

  bodies.forEach((body, index) => {
    if (body.parent_id) return

    const r = orbitRadius(body.orbit, index, maxR, bodyCount)
    drawOrbit(ctx, cx, cy, r, scale, body.orbit)
  })

  bodies.forEach((body, index) => {
    if (!body.parent_id) return

    const parentPos = bodyPositions.get(body.parent_id)
    if (!parentPos) return

    const r = orbitRadius(body.orbit, index, maxR, bodyCount)
    drawOrbit(ctx, parentPos.x, parentPos.y, r, scale, body.orbit)
  })

  bodies.forEach((body) => {
    const pos = bodyPositions.get(body.id)
    if (!pos) return
    const isPlayer = body.id === playerOrbiterId
    drawBody(ctx, pos.x, pos.y, body, isPlayer)
  })
}

export function renderSolarSystem (canvas, system, options = {}) {
  if (!canvas || !system) return

  const ctx = canvas.getContext('2d')
  const dpr = window.devicePixelRatio || 1
  const rect = canvas.getBoundingClientRect()
  const width = rect.width
  const height = rect.height

  canvas.width = width * dpr
  canvas.height = height * dpr
  ctx.scale(dpr, dpr)

  ctx.fillStyle = '#0a0e17'
  ctx.fillRect(0, 0, width, height)

  const cx = width / 2
  const cy = height / 2
  const star = system.star
  const bodies = system.bodies || []
  const bodyCount = bodies.length

  const maxR = maxOrbitRadius(bodies)
  const scale = scaleFactor(width, height, maxR)
  const bodyPositions = computeBodyPositions(cx, cy, bodies, maxR, bodyCount, scale)

  const playerPos = bodyPositions.get(system.player_orbiter_id)
  const focusX = playerPos?.x ?? cx
  const focusY = playerPos?.y ?? cy
  const zoom = options.zoom ?? 1

  if (zoom !== 1) {
    ctx.save()
    ctx.translate(focusX, focusY)
    ctx.scale(zoom, zoom)
    ctx.translate(-focusX, -focusY)
  }

  drawSolarSystemContent(ctx, cx, cy, star, bodies, maxR, bodyCount, scale, bodyPositions, system.player_orbiter_id)

  if (zoom !== 1) {
    ctx.restore()
  }
}
